import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { authGet } from '../../../../services/apiService';
import { AzureDetail, AzureSummary } from './AzureView';
import { YearDetail, YearSummary } from './YearView';
import CaseRegister from './CaseRegister';
import IncidentsView from './IncidentsView';
import PerformanceView from './PerformanceView';
import { fmtDayLong, fmtInt, periodLine } from './briefingFormat';
import './briefing.css';

const MODES = [
  { key: 'yr', label: '2025 and 2026' },
  { key: 'az', label: '2026 · Azure pre / post' },
  { key: 'inc', label: 'Incidents' },
  { key: 'perf', label: 'Client performance' }
];

const TABS = [
  { key: 'summary', label: 'Summary' },
  { key: 'detail', label: 'Detailed' },
  { key: 'register', label: 'Case register' }
];

const TabBar = ({ tab, onChange }) => (
  <div className="tabbar" role="tablist">
    {TABS.map((item) => (
      <button
        type="button"
        role="tab"
        key={item.key}
        aria-selected={tab === item.key ? 'true' : 'false'}
        onClick={() => onChange(item.key)}
      >
        {item.label}
      </button>
    ))}
  </div>
);

/** Report views that exist for this briefing: the two case views always, the curated ones when their files do. */
const availableModes = (briefing) => MODES.filter((item) => {
  if (item.key === 'inc') return Array.isArray(briefing.incidents) && briefing.incidents.length > 0;
  if (item.key === 'perf') return Boolean(briefing.performance);
  return true;
});

const Panel = ({ name, mode, tab, briefing, study, onStudy }) => {
  if (mode === 'inc') return <IncidentsView key={name} name={name} incidents={briefing.incidents} />;
  if (mode === 'perf') return <PerformanceView performance={briefing.performance} />;
  if (tab === 'register') {
    const html = briefing.html || {};
    return (
      <CaseRegister
        key={`${mode}-${study.stamp}`}
        mode={mode}
        cases={(mode === 'yr' ? html.yrCases : html.azCases) || []}
        initial={mode === 'az' ? study.filters : null}
        cutover={fmtDayLong(briefing.periods.azureCutover)}
        periods={briefing.periods}
      />
    );
  }
  if (mode === 'yr') return tab === 'summary' ? <YearSummary briefing={briefing} /> : <YearDetail briefing={briefing} />;
  return tab === 'summary'
    ? <AzureSummary briefing={briefing} onStudy={onStudy} activeSymptom={(study.filters && study.filters.symptom) || ''} />
    : <AzureDetail briefing={briefing} />;
};

/** Tells the reader when hand-written content was not deployed, so a gap is never silent. */
const MissingContent = ({ files }) => (Array.isArray(files) && files.length > 0 ? (
  <p className="note" role="alert">
    <b>Some briefing content is not on this server:</b> {files.join(', ')}. Add them under server/customer-briefings (or the data override folder).
  </p>
) : null);

/** Short guide to the views; the Incidents and Client performance lines show only when that content exists. */
const HowTo = ({ briefing }) => (
  <p className="howto">
    <b>What this page covers.</b> It opens on 2026, before and after the {fmtDayLong(briefing.periods.azureCutover)} move
    to Azure. 2025 and 2026 compares the two years.
    {briefing.performance ? ' Client performance is the Apache view by month: response time, rate limits, and server errors.' : ''}
    {Array.isArray(briefing.incidents) && briefing.incidents.length > 0 ? ' Incidents holds the incident write-ups for this meeting.' : ''}
    {' '}Green is better for the service, red is worse. Counts in the case views are per 30 days, so a short month can be
    compared with a full one.
  </p>
);

/**
 * Leadership briefing, opened from the Top Customers card at /briefings/<account name>.
 * The numbers come from the briefing API with the portal's own login, so there is no separate sign-in.
 */
export default function CustomerBriefingPage() {
  const { name } = useParams();
  const [briefing, setBriefing] = useState(null);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('az');
  const [tab, setTab] = useState('summary');
  const [study, setStudy] = useState({ stamp: 0, filters: null });

  useEffect(() => {
    let cancelled = false;
    setBriefing(null);
    setError('');
    (async () => {
      try {
        const res = await authGet(`/api/briefings/${encodeURIComponent(name)}`, null, 'json', { useCache: false });
        if (cancelled) return;
        if (!res || res.success === false || !res.briefing) {
          throw new Error((res && res.message) || 'Briefing response missing');
        }
        setBriefing(res.briefing);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load the briefing');
      }
    })();
    return () => { cancelled = true; };
  }, [name]);

  useEffect(() => {
    if (briefing) document.title = `${briefing.customer.name} — engineering briefing`;
  }, [briefing]);

  // Clicking a chart segment or legend entry lists those cases in the register
  const handleStudy = useCallback((symptom, period) => {
    setStudy((prev) => ({ stamp: prev.stamp + 1, filters: { symptom: symptom || '', period: period || '' } }));
    setMode('az');
    setTab('register');
  }, []);

  const changeMode = (next) => {
    setMode(next);
    setTab('summary');
  };

  const customerName = briefing ? briefing.customer.name : '';
  const lake = briefing && briefing.coverage && briefing.coverage.lake;

  return (
    <div className="cb">
      <div className="sheet">
        <div className="bar" />
        <div className="inner">
          <header className="mast">
            <div>
              <div className="wordmark">HighQ - Thomson Reuters</div>
              <h1>{customerName || 'Engineering briefing'}</h1>
              {briefing && (mode === 'yr' || mode === 'az')
                ? <p className="period">{periodLine(briefing.periods, mode)}</p>
                : null}
              <p className="audience">Engineering briefing{customerName ? ` for the meeting with ${customerName}` : ''}</p>
            </div>
            <div className="mast-side">
              <div className="conf">Confidential</div>
              <div className="modebar" role="tablist" aria-label="Report">
                {(briefing ? availableModes(briefing) : MODES.slice(0, 2)).map((item) => (
                  <button
                    type="button"
                    key={item.key}
                    aria-selected={mode === item.key ? 'true' : 'false'}
                    onClick={() => changeMode(item.key)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </header>

          {!briefing && !error ? <p className="note" role="status">Loading Data Lake cases&hellip;</p> : null}
          {error ? <p className="note" role="alert">Could not load the briefing: {error}</p> : null}

          {briefing ? (
            <>
              <MissingContent files={briefing.missingContent} />
              <HowTo briefing={briefing} />
              <p className="note">
                Data Lake: {fmtInt(lake && lake.y25)} cases in 2025 &middot; {fmtInt(lake && lake.y26)} in 2026
              </p>
              {mode === 'yr' || mode === 'az' ? <TabBar tab={tab} onChange={setTab} /> : null}
              <Panel name={name} mode={mode} tab={tab} briefing={briefing} study={study} onStudy={handleStudy} />
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
